import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { ownListingForNav } from '../../util/ownListeningProfile';
import {
  canSwitchNavMode,
  resolveNavMode,
  switchNavModeLocation,
} from '../../util/siteNavMode';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { savePreferredNavMode } from '../../ducks/user.duck';

import ModeSwitchToggle from '../ModeSwitchToggle/ModeSwitchToggle';

import css from './DualRoleModeSwitch.module.css';

/**
 * Same Listener | Talker slide toggle used on the dashboard and in the
 * menu. Dual-role only. Single-role and listener-only pages omit this.
 *
 * @param {Object} props
 * @param {string} props.currentPage
 * @param {string} [props.className]
 * @param {boolean} [props.isOwnListing]
 * @returns {JSX.Element|null}
 */
const DualRoleModeSwitch = props => {
  const { currentPage, className, isOwnListing: isOwnListingProp } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const currentUser = useSelector(state => state.user?.currentUser);
  const currentUserHasListings = useSelector(state => !!state.user?.currentUserHasListings);
  const currentUserHasListingsFetched = useSelector(
    state => state.user?.currentUserHasListingsFetched
  );
  const currentUserOwnListing = useSelector(state =>
    ownListingForNav(
      state.user?.currentUserOwnListing,
      getMarketplaceEntities(state, state.ProfilePage?.userListingRefs || [])
    )
  );
  const listingId =
    currentUserOwnListing?.id?.uuid ||
    (typeof currentUserOwnListing?.id === 'string' ? currentUserOwnListing.id : null);
  const isOwnListing =
    isOwnListingProp === true ||
    (!!listingId &&
      typeof location?.pathname === 'string' &&
      /\/l\//.test(location.pathname) &&
      location.pathname.includes(listingId));
  const layout = {
    currentUser,
    currentUserHasListings,
    currentUserHasListingsFetched,
    currentUserOwnListing,
    currentPage,
    search: location?.search,
    isOwnListing,
  };

  if (!canSwitchNavMode(layout)) {
    return null;
  }

  const mode = resolveNavMode(layout);
  const onChange = next => {
    Promise.resolve(dispatch(savePreferredNavMode(next))).catch(() => {});
    const dest = switchNavModeLocation(next, {
      currentPage,
      search: location?.search,
      hash: location?.hash,
      currentUser,
      currentUserOwnListing,
    });
    history.push(
      createResourceLocatorString(
        dest.name,
        routeConfiguration,
        dest.params || {},
        dest.searchParams || {},
        dest.hash || ''
      )
    );
  };

  return (
    <div
      className={classNames(css.root, className)}
      data-dual-role-switch={currentPage || 'page'}
      data-position="static"
    >
      <ModeSwitchToggle mode={mode} onChange={onChange} />
    </div>
  );
};

export default DualRoleModeSwitch;
